import { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  Flex,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import type { Client } from 'src/admin/users/types';
import { useAdvisors } from 'src/clients/hooks/useAdvisors';
import { useClients } from 'src/clients/hooks/useClients';
import { Button } from 'src/design-system/Button';
import { Modal } from 'src/design-system/Modal';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useMeetingParticipants } from 'src/meetings/hooks/useMeetingParticipants';
import type { FamilyMeeting } from 'src/meetings/hooks/useScopedMeetings';
import type { MeetingScope } from 'src/meetings/scope';
import { AttendeeRow } from './AttendeeRow';
import { type Attendee, type AttendeeGroup, useMeetingAttendees } from './useMeetingAttendees';
import {
  type ProfessionalConnection,
  useProfessionalConnections,
} from './useProfessionalConnections';

type Props = {
  opened: boolean;
  onClose: () => void;
  scope: MeetingScope;
  meeting: FamilyMeeting;
};

type Section = {
  group: AttendeeGroup;
  title: string;
  addLabel: string;
  searchPlaceholder: string;
  noneLabel: string;
  addAsWord: string;
};

const SECTIONS: Section[] = [
  {
    group: 'adviser',
    title: 'Advisers',
    addLabel: 'Add adviser',
    searchPlaceholder: 'Search advisers',
    noneLabel: 'No advisers found',
    addAsWord: 'guest',
  },
  {
    group: 'guest',
    title: 'Guests',
    addLabel: 'Add guest',
    searchPlaceholder: 'Search clients',
    noneLabel: 'No such clients',
    addAsWord: 'guest',
  },
  {
    group: 'keyContact',
    title: 'Key contacts',
    addLabel: 'Add key contact',
    searchPlaceholder: 'Search professional connections',
    noneLabel: 'No such professional connections',
    addAsWord: 'key contact',
  },
];

const REQUIRED_GROUP_MESSAGES: Partial<Record<AttendeeGroup, string>> = {
  adviser: 'At least one adviser should be selected',
  guest: 'At least one guest should be selected',
};

const addLinkStyle: React.CSSProperties = {
  color: 'var(--mantine-color-brand-6)',
  fontFamily: 'Manrope',
  fontSize: 12,
  fontWeight: 600,
  lineHeight: '18px',
  textDecorationLine: 'underline',
  textDecorationStyle: 'solid',
  textDecorationSkipInk: 'none',
  textDecorationThickness: '0.9px',
  textUnderlineOffset: '1.56px',
  textUnderlinePosition: 'from-font',
};

const addResultStyle: React.CSSProperties = {
  color: 'var(--mantine-color-brand-6)',
  fontFamily: 'Manrope',
  fontSize: 12,
  fontWeight: 600,
  lineHeight: '18px',
};

const boxStyle: React.CSSProperties = {
  borderRadius: 16,
  border: '1px solid var(--mantine-color-gray-1)',
  padding: 8,
};

function fullName(person: { firstName: string; lastName: string }): string {
  return `${person.firstName} ${person.lastName}`.trim();
}

function adviserToAttendee(advisor: { id: string; firstName: string; lastName: string }): Attendee {
  return {
    id: `user:${advisor.id}`,
    name: fullName(advisor),
    role: 'Adviser',
    group: 'adviser',
    participantId: null,
    defaultChecked: false,
    addBody: { participantType: 'USER', userId: advisor.id },
  };
}

function clientToAttendee(client: Client, group: AttendeeGroup): Attendee {
  return {
    id: `client:${client.id}`,
    name: fullName(client),
    role: 'Client',
    group,
    participantId: null,
    defaultChecked: false,
    addBody: { participantType: 'CLIENT', clientId: client.id },
  };
}

function connectionToAttendee(connection: ProfessionalConnection): Attendee {
  const role = connection.type || 'Key contact';
  return {
    id: `conn:${connection.id}`,
    name: connection.name,
    role,
    group: 'keyContact',
    participantId: null,
    defaultChecked: false,
    addBody: { participantType: 'EXTERNAL', name: connection.name, description: role },
  };
}

function matchesSearch(haystack: string, search: string): boolean {
  const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const value = haystack.toLowerCase();
  return words.every(word => value.includes(word));
}

type CustomAttendee = { id: string; name: string; role: string; group: AttendeeGroup };

export function AddExpectedAttendeesModal({ opened, onClose, scope, meeting }: Props) {
  const meetingAttendees = useMeetingAttendees(scope, meeting);
  const { advisors } = useAdvisors();
  const { clients } = useClients();
  const connections = useProfessionalConnections(scope);
  const { addParticipant, removeParticipant } = useMeetingParticipants(scope);
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const [addedAdviserIds, setAddedAdviserIds] = useState<string[]>([]);
  const [addedClients, setAddedClients] = useState<{ clientId: string; group: AttendeeGroup }[]>(
    [],
  );
  const [addedConnectionIds, setAddedConnectionIds] = useState<string[]>([]);
  const [customAttendees, setCustomAttendees] = useState<CustomAttendee[]>([]);
  const [customSeq, setCustomSeq] = useState(0);
  const [openSection, setOpenSection] = useState<AttendeeGroup | null>(null);
  const [search, setSearch] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [validationTriggered, setValidationTriggered] = useState(false);

  // The modal stays mounted at some call sites, so clear all local selection
  // state once it closes to avoid stale checkboxes and re-adding saved entries.
  useEffect(() => {
    if (opened) return;
    setOverrides({});
    setAddedAdviserIds([]);
    setAddedClients([]);
    setAddedConnectionIds([]);
    setCustomAttendees([]);
    setCustomSeq(0);
    setOpenSection(null);
    setSearch('');
    setValidationTriggered(false);
  }, [opened]);

  const meetingIds = new Set(meetingAttendees.map(attendee => attendee.id));

  const addedAdviserAttendees: Attendee[] = advisors
    .filter(advisor => addedAdviserIds.includes(advisor.id))
    .map(adviserToAttendee)
    .filter(attendee => !meetingIds.has(attendee.id));

  const clientById = new Map(clients.map(client => [client.id, client]));
  const addedClientAttendees: Attendee[] = addedClients
    .map(({ clientId, group }) => {
      const client = clientById.get(clientId);
      return client ? clientToAttendee(client, group) : null;
    })
    .filter((attendee): attendee is Attendee => !!attendee && !meetingIds.has(attendee.id));

  const connectionById = new Map(connections.map(connection => [connection.id, connection]));
  const addedConnectionAttendees: Attendee[] = addedConnectionIds
    .map(id => connectionById.get(id))
    .filter((connection): connection is ProfessionalConnection => !!connection)
    .map(connectionToAttendee);

  const customAsAttendees: Attendee[] = customAttendees.map(custom => ({
    id: custom.id,
    name: custom.name,
    role: custom.role,
    group: custom.group,
    participantId: null,
    defaultChecked: false,
    addBody: { participantType: 'EXTERNAL', name: custom.name, description: custom.role },
  }));

  const attendees = [
    ...meetingAttendees,
    ...addedAdviserAttendees,
    ...addedClientAttendees,
    ...addedConnectionAttendees,
    ...customAsAttendees,
  ];

  const isChecked = (attendee: Attendee) => overrides[attendee.id] ?? attendee.defaultChecked;

  const toggle = (attendee: Attendee) =>
    setOverrides(previous => ({
      ...previous,
      [attendee.id]: !(previous[attendee.id] ?? attendee.defaultChecked),
    }));

  const shownIds = new Set(attendees.map(attendee => attendee.id));
  const shownKeyContactNames = new Set(
    attendees
      .filter(attendee => attendee.group === 'keyContact')
      .map(attendee => attendee.name.trim().toLowerCase()),
  );

  const adviserResults = advisors
    .filter(advisor => !shownIds.has(`user:${advisor.id}`))
    .filter(advisor => matchesSearch(`${fullName(advisor)} ${advisor.emailAddress}`, search));

  const clientResults = clients
    .filter(client => !shownIds.has(`client:${client.id}`))
    .filter(client => matchesSearch(`${fullName(client)} ${client.emailAddress ?? ''}`, search));

  const connectionResults = connections
    .filter(connection => !shownIds.has(`conn:${connection.id}`))
    .filter(connection => !shownKeyContactNames.has(connection.name.trim().toLowerCase()))
    .filter(connection => matchesSearch(`${connection.name} ${connection.email}`, search));

  function selectAdviser(advisorId: string) {
    setAddedAdviserIds(previous => [...previous, advisorId]);
    setOverrides(previous => ({ ...previous, [`user:${advisorId}`]: true }));
    setSearch('');
  }

  function selectClient(clientId: string, group: AttendeeGroup) {
    setAddedClients(previous => [...previous, { clientId, group }]);
    setOverrides(previous => ({ ...previous, [`client:${clientId}`]: true }));
    setSearch('');
  }

  function selectConnection(connectionId: string) {
    setAddedConnectionIds(previous => [...previous, connectionId]);
    setOverrides(previous => ({ ...previous, [`conn:${connectionId}`]: true }));
    setSearch('');
  }

  function addCustomAttendee(group: AttendeeGroup) {
    const name = search.trim();
    if (!name) return;
    const id = `custom:${customSeq}`;
    setCustomSeq(previous => previous + 1);
    setCustomAttendees(previous => [...previous, { id, name, role: '', group }]);
    setOverrides(previous => ({ ...previous, [id]: true }));
    setSearch('');
  }

  function setCustomRole(id: string, role: string) {
    setCustomAttendees(previous =>
      previous.map(custom => (custom.id === id ? { ...custom, role } : custom)),
    );
  }

  function closeSearch() {
    setOpenSection(null);
    setSearch('');
  }

  function handleClose() {
    closeSearch();
    onClose();
  }

  const hasCheckedInGroup = (group: AttendeeGroup) =>
    attendees.some(attendee => attendee.group === group && isChecked(attendee));

  async function handleAdd() {
    const missingRequiredGroup = (Object.keys(REQUIRED_GROUP_MESSAGES) as AttendeeGroup[]).some(
      group => !hasCheckedInGroup(group),
    );
    if (missingRequiredGroup) {
      setValidationTriggered(true);
      return;
    }

    const operations: Promise<unknown>[] = [];
    for (const attendee of attendees) {
      const checked = isChecked(attendee);
      if (checked && !attendee.defaultChecked) {
        operations.push(
          addParticipant.mutateAsync({ eventId: meeting.id, body: attendee.addBody }),
        );
      } else if (!checked && attendee.defaultChecked && attendee.participantId) {
        operations.push(
          removeParticipant.mutateAsync({
            eventId: meeting.id,
            participantId: attendee.participantId,
          }),
        );
      }
    }

    if (operations.length === 0) {
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      await Promise.all(operations);
    } catch (error) {
      setIsSaving(false);
      notifyAndLogError(
        'Failed to update attendees',
        'Could not update the meeting attendees. Please try again.',
        error,
      );
      return;
    }
    setIsSaving(false);
    onClose();
  }

  function addPill(label: string, onAdd: () => void) {
    return (
      <UnstyledButton
        style={{ ...addResultStyle, position: 'absolute', top: 4, right: 4 }}
        onClick={onAdd}
      >
        <Flex align="center" gap={2}>
          {label}
          <IconPlus size={16} stroke={2.5} color="var(--mantine-color-brand-6)" />
        </Flex>
      </UnstyledButton>
    );
  }

  function renderRow(attendee: Attendee) {
    if (attendee.id.startsWith('custom:')) {
      return (
        <Flex key={attendee.id} align="center" gap="sm" py="xxxs">
          <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10" style={{ width: 160 }}>
            {attendee.name}
          </Text>
          <TextInput
            size="md"
            placeholder="Add role"
            value={attendee.role}
            onChange={event => setCustomRole(attendee.id, event.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Checkbox
            checked={isChecked(attendee)}
            onChange={() => toggle(attendee)}
            aria-label={attendee.name}
          />
        </Flex>
      );
    }
    return (
      <AttendeeRow
        key={attendee.id}
        name={attendee.name}
        role={attendee.role}
        checked={isChecked(attendee)}
        onToggle={() => toggle(attendee)}
        ariaLabel={`${attendee.name} attending`}
      />
    );
  }

  function renderResultRow(id: string, name: string, email: string, onAdd: () => void) {
    return (
      <Box key={id} style={{ position: 'relative' }}>
        <Text size="sm" fw={FONT_WEIGHTS.semibold} c="black">
          {name}
        </Text>
        <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.7" mt="xxxs">
          {email}
        </Text>
        {addPill('Add', onAdd)}
      </Box>
    );
  }

  if (openSection) {
    const section = SECTIONS.find(item => item.group === openSection)!;
    const canAddFreeText = section.group !== 'adviser';

    let results: { id: string; name: string; email: string; onAdd: () => void }[];
    if (section.group === 'adviser') {
      results = adviserResults.map(advisor => ({
        id: advisor.id,
        name: fullName(advisor),
        email: advisor.emailAddress,
        onAdd: () => selectAdviser(advisor.id),
      }));
    } else if (section.group === 'guest') {
      results = clientResults.map(client => ({
        id: client.id,
        name: fullName(client),
        email: client.emailAddress ?? '',
        onAdd: () => selectClient(client.id, 'guest'),
      }));
    } else {
      results = connectionResults.map(connection => ({
        id: connection.id,
        name: connection.name,
        email: connection.email,
        onAdd: () => selectConnection(connection.id),
      }));
    }

    const showEmptyAdd = canAddFreeText && search.trim().length > 0 && results.length === 0;

    return (
      <Modal opened={opened} onClose={handleClose} size={440}>
        <Text size="displaySm" fw={FONT_WEIGHTS.semibold} c="black" mb="lg">
          Add expected attendees
        </Text>

        <Box style={boxStyle}>
          <Flex align="center" justify="space-between">
            <Text size="sm" fw={FONT_WEIGHTS.semibold} c="black">
              {section.title}
            </Text>
            <UnstyledButton style={addResultStyle} onClick={closeSearch}>
              Done
            </UnstyledButton>
          </Flex>

          <Stack gap="xxxs" mt={10}>
            {attendees.filter(attendee => attendee.group === section.group).map(renderRow)}
          </Stack>

          <TextInput
            mt="xs"
            value={search}
            onChange={event => setSearch(event.currentTarget.value)}
            placeholder={section.searchPlaceholder}
            leftSection={<IconSearch size={16} />}
            leftSectionWidth={32}
            styles={{ input: { height: 36, minHeight: 36, borderRadius: 12 } }}
          />

          <Box mt={6}>
            <ScrollArea.Autosize mah={260} type="auto">
              <Stack gap="xxs" px="xs" py="xxs">
                {results.map(result =>
                  renderResultRow(result.id, result.name, result.email, result.onAdd),
                )}

                {!canAddFreeText && results.length === 0 && (
                  <Text size="sm" c="gray.6">
                    {section.noneLabel}
                  </Text>
                )}

                {showEmptyAdd && (
                  <Box style={{ position: 'relative' }}>
                    <Text size="sm" fw={FONT_WEIGHTS.semibold} c="black">
                      {section.noneLabel}
                    </Text>
                    <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.7" mt="xxxs">
                      But you can still add as a {section.addAsWord}
                    </Text>
                    {addPill(`Add ${search.trim()}`, () => addCustomAttendee(section.group))}
                  </Box>
                )}
              </Stack>
            </ScrollArea.Autosize>
          </Box>
        </Box>

        <Box mt="lg">
          <Button fullWidth disabled>
            Click done before saving attendees
          </Button>
        </Box>
      </Modal>
    );
  }

  return (
    <Modal opened={opened} onClose={handleClose} size={440}>
      <Text size="displaySm" fw={FONT_WEIGHTS.semibold} c="black" mb="lg">
        Add expected attendees
      </Text>
      <Stack gap="lg">
        {SECTIONS.map(section => (
          <Box key={section.group}>
            <Flex align="center" justify="space-between" px="xxs" pb={10}>
              <Text size="sm" fw={FONT_WEIGHTS.semibold} c="black">
                {section.title}
              </Text>
              <UnstyledButton style={addLinkStyle} onClick={() => setOpenSection(section.group)}>
                {section.addLabel}
              </UnstyledButton>
            </Flex>

            <Stack gap="xxxs" p="xxs">
              {attendees.filter(attendee => attendee.group === section.group).map(renderRow)}
            </Stack>

            {validationTriggered &&
              REQUIRED_GROUP_MESSAGES[section.group] &&
              !hasCheckedInGroup(section.group) && (
                <Text size="sm" fw={FONT_WEIGHTS.regular} c="error.6" mt="xxs">
                  {REQUIRED_GROUP_MESSAGES[section.group]}
                </Text>
              )}
          </Box>
        ))}
      </Stack>

      <Box mt="lg">
        <Button fullWidth onClick={handleAdd} loading={isSaving} disabled={isSaving}>
          Add
        </Button>
      </Box>
    </Modal>
  );
}
